import { useState, useEffect, useCallback } from 'react';

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Tooltip,
  Typography,
} from '@mui/material';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PhoneIcon from '@mui/icons-material/Phone';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { moment, SYSTEM_TIMEZONE } from '../../utils/dateUtils';

import appointmentsServices from '../../services/appointmentsServices';

const formatTime = (value) =>
  value ? moment(value).tz(SYSTEM_TIMEZONE).format('h:mm A') : null;

// Technicians closest to this appointment's address by real driving time.
// Positions come from live vehicle GPS when available, otherwise from the
// address of the job the technician is on today.
const NearestTechModal = ({ open, onClose, appointmentId, address }) => {
  const [kind, setKind] = useState('technician');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await appointmentsServices.getNearestTechnicians(appointmentId, 5, kind);
      setResult(response.data);
    } catch (err) {
      console.error('Nearest technician lookup failed:', err);
      setError(err?.response?.data?.message || 'Could not find nearest technicians');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [appointmentId, kind]);

  useEffect(() => {
    if (open && appointmentId) {
      load();
    }
  }, [open, appointmentId, load]);

  const technicians = result?.technicians || [];
  const skipped = result?.skipped || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Nearest technicians
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {address || result?.destination?.address}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={kind}
          onChange={(event, value) => value && setKind(value)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="technician" sx={{ textTransform: 'none', px: 2 }}>
            Technicians
          </ToggleButton>
          <ToggleButton value="salesman" sx={{ textTransform: 'none', px: 2 }}>
            Salesmen
          </ToggleButton>
        </ToggleButtonGroup>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && technicians.length === 0 && (
          <Alert severity="info">
            No {kind === 'salesman' ? 'salesmen' : 'technicians'} with a known location right now.
          </Alert>
        )}

        {!loading && !error && technicians.length > 0 && (
          <List disablePadding>
            {technicians.map((tech, index) => (
              <ListItem
                key={tech.id}
                divider={index < technicians.length - 1}
                sx={{
                  alignItems: 'flex-start',
                  gap: 1.5,
                  py: 1.5,
                  ...(tech.nextJob?.exceedsLimit && {
                    bgcolor: '#fdecea',
                    borderLeft: '4px solid',
                    borderLeftColor: 'error.main',
                  }),
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 14,
                    bgcolor: tech.nextJob?.exceedsLimit
                      ? 'error.main'
                      : index === 0
                        ? 'success.main'
                        : 'grey.400',
                  }}
                >
                  {index + 1}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600 }}>{tech.fullName}</Typography>
                    <Chip
                      size="small"
                      icon={tech.positionSource === 'gps' ? <GpsFixedIcon /> : <EventNoteIcon />}
                      label={tech.positionSource === 'gps' ? 'Live GPS' : 'From schedule'}
                      color={tech.positionSource === 'gps' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>

                  <Tooltip title={tech.positionLabel || ''}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tech.positionLabel}
                      {tech.positionAt ? ` · ${formatTime(tech.positionAt)}` : ''}
                    </Typography>
                  </Tooltip>

                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {tech.jobsToday} job{tech.jobsToday === 1 ? '' : 's'} today
                    {tech.phoneNumber ? ` · ${tech.phoneNumber}` : ''}
                  </Typography>

                  {tech.nextJob && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.25,
                        fontWeight: tech.nextJob.exceedsLimit ? 600 : 400,
                        color: tech.nextJob.exceedsLimit ? 'error.main' : 'text.secondary',
                      }}
                    >
                      {tech.nextJob.exceedsLimit && <WarningAmberIcon sx={{ fontSize: 14 }} />}
                      Next job {formatTime(tech.nextJob.startTime)} is{' '}
                      {tech.nextJob.minutesFromDestination} min away
                    </Typography>
                  )}
                </Box>

                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {tech.etaMinutes} min
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {tech.distanceMiles} mi
                  </Typography>
                  {tech.phoneNumber && (
                    <Box>
                      <Button
                        size="small"
                        startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                        href={`tel:${tech.phoneNumber}`}
                        sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }}
                      >
                        Call
                      </Button>
                    </Box>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        {!loading && !error && technicians.length > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
            Showing the {technicians.length} closest of {result.availableCount}{' '}
            {kind === 'salesman' ? 'salesmen' : 'technicians'} with a known location. Red marks
            anyone whose next job is more than {result.nextJobWarningMinutes} min from this address.
          </Typography>
        )}

        {!loading && !error && skipped.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Excluded ({skipped.length})
            </Typography>
            {skipped.map((tech) => (
              <Typography key={tech.id} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {tech.fullName} — {tech.reason}
              </Typography>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NearestTechModal;
